/* eslint-disable no-console */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Grid,
  Box,
  Modal as MuiModal,
  IconButton,
  Tooltip
} from "@mui/material";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import Button from "../../../components/Button";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import InputGroup from "../../../components/FormComponent/InputGroup";
import { TableSearch } from "../../../components/Table/TableActions";
import useStyles from "../bulkstyles";

const BulkResultEntry = ({
  open,
  onClose,
  classId,
  className = "",
  termId = "",
  termName = "",
  session = "",
  subjects = [],
  students = [],
  obtainableScores = [],
  gradingSystems = [],
  existingResults = [],
  classTestResults = [],
  classExamResults = [],
  onSave,
  isLoading = false,
  initialGradingSystemId = ""
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [selectedGradingSystem, setSelectedGradingSystem] = useState(initialGradingSystemId);
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [importCAScores, setImportCAScores] = useState(false);
  const [importExamScores, setImportExamScores] = useState(false);
  const [results, setResults] = useState({});
  const [scoreErrors, setScoreErrors] = useState({});
  const [gradingSystem, setGradingSystem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Get CA columns from obtainable scores
  const caColumns = obtainableScores?.[0]?.ca || [];
  const caCount = caColumns.length || 0;
  const fixedCols = 4; // CA_total + Exam + Total + Grade
  const colSpan = caCount + fixedCols;

  // Function to get student ID
  const getStudentId = (student) => {
    return student?.id ||
           student?.student?.student_id ||
           student?.student?.id ||
           student?.student_id;
  };

  // Function to get student name
  const getStudentName = (student) => {
    return student?.name ||
           student?.student?.student_name ||
           `${student?.student?.last_name || ""} ${student?.student?.first_name || ""}`.trim() ||
           `${student?.last_name || ""} ${student?.first_name || ""}`.trim() ||
           "";
  };

  // Function to get subject ID
  const getSubjectId = (subject) => {
    return subject?.id || subject?.subject_id;
  };

  // Function to get subject name
  const getSubjectName = (subject) => {
    return subject?.name || subject?.subject_name || "";
  };

  // Filter subjects based on selected subject filter
  const filteredSubjects = useMemo(() => {
    if (selectedSubject === "all") {
      return subjects;
    }

    return subjects.filter(subject => {
      const subjectId = getSubjectId(subject);

      return subjectId === selectedSubject;
    });
  }, [subjects, selectedSubject]);

  // Filter students based on search query
  const filteredStudents = useMemo(() => {
    if (!searchQuery?.trim()) {
      return Object?.keys(results);
    }

    const query = searchQuery?.toLowerCase();

    return Object.keys(results)?.filter(studentId => {
      const student = results[studentId];

      return student.student_name?.toLowerCase()?.includes(query);
    });
  }, [results, searchQuery]);

  // Calculate colSpan for filtered subjects
  const filteredColSpan = filteredSubjects?.length > 0 ? (caCount + fixedCols) * filteredSubjects?.length : colSpan;

  // Function to create an empty CA scores structure
  const createEmptyCAScores = () => {
    const caScores = {};
    if (caColumns?.length > 0) {
      caColumns?.forEach(ca => {
        caScores[ca?.ca_type] = "";
      });
    }

    return caScores;
  };

  // Function to parse existing results into a structured format
  const parseExistingResults = useCallback(() => {
    const parsedResults = {};

    if (!existingResults || existingResults.length === 0) {
      return parsedResults;
    }

    existingResults.forEach(subjectData => {
      const subjectId = getSubjectId(subjectData?.subject);
      const subjectName = getSubjectName(subjectData?.subject);
      const studentsList = subjectData.students || [];

      studentsList.forEach(studentData => {
        const studentId = getStudentId(studentData);
        const studentName = getStudentName(studentData);
        const scores = studentData?.scores || {};

        if (!parsedResults[studentId]) {
          parsedResults[studentId] = {
            student_id: studentId,
            student_name: studentName,
            subjects: {}
          };
        }

        // Initialize subject entry if not exists
        if (!parsedResults[studentId]?.subjects[subjectId]) {
          parsedResults[studentId].subjects[subjectId] = {
            subject_id: subjectId,
            subject_name: subjectName,
            CA_scores: createEmptyCAScores(),
            CA_total: 0,
            exam_score: "",
            total: 0,
            grade: "",
            grading_system_id: selectedGradingSystem
          };
        }

        // Fill CA scores from existing results
        const caScores = {};
        if (scores?.CA_scores) {
          // Map existing CA scores to the structure
          Object?.keys(scores?.CA_scores)?.forEach(caKey => {
            // Find matching CA column from obtainable scores
            const matchingCA = caColumns?.find(ca => ca?.ca_type?.toLowerCase() === caKey?.toLowerCase()?.replace(/\s+/g, "")
            );

            if (matchingCA) {
              caScores[matchingCA?.ca_type] = scores?.CA_scores[caKey] || "";
            } else {
              // If no exact match, try to find similar
              const caType = caColumns?.find(ca => caKey?.includes(ca?.ca_type) || ca?.ca_type?.includes(caKey)
              )?.ca_type || caKey;
              caScores[caType] = scores?.CA_scores[caKey] || "";
            }
          });

          // Ensure all required CA columns are present
          caColumns?.forEach(ca => {
            if (!caScores[ca?.ca_type]) {
              caScores[ca?.ca_type] = "";
            }
          });
        }

        // Calculate CA total if not provided
        const caTotal = scores?.CA_total ||
          Object.values(caScores)?.reduce((sum, val) => sum + (Number(val) || 0), 0);

        const examScore = scores?.exam_score || "";
        const totalScore = scores?.total || (caTotal + (Number(examScore) || 0));
        const grade = scores?.grade || "";
        const gradingSystemId = scores?.grading_system_id || selectedGradingSystem;

        parsedResults[studentId].subjects[subjectId] = {
          subject_id: subjectId,
          subject_name: subjectName,
          CA_scores: caScores,
          CA_total: caTotal,
          exam_score: examScore,
          total: totalScore,
          grade,
          grading_system_id: gradingSystemId
        };
      });
    });

    return parsedResults;
  }, [existingResults, caColumns, selectedGradingSystem]);

  // Function to initialize empty results for all students and all subjects
  const initializeEmptyResults = useCallback(() => {
    const initialResults = {};

    students.forEach((student) => {
      const studentId = getStudentId(student);
      const studentName = getStudentName(student);

      initialResults[studentId] = {
        student_id: studentId,
        student_name: studentName,
        subjects: {}
      };

      // Initialize each subject for this student
      subjects.forEach((subject) => {
        const subjectId = getSubjectId(subject);
        const subjectName = getSubjectName(subject);

        // Get CA scores structure from obtainable scores
        const caScores = {};
        if (caColumns.length > 0) {
          caColumns?.forEach(ca => {
            caScores[ca?.ca_type] = "";
          });
        }

        initialResults[studentId].subjects[subjectId] = {
          subject_id: subjectId,
          subject_name: subjectName,
          CA_scores: caScores,
          CA_total: 0,
          exam_score: "",
          total: 0,
          grade: "",
          grading_system_id: selectedGradingSystem
        };
      });
    });

    return initialResults;
  }, [students, subjects, caColumns, selectedGradingSystem]);

  const fillWithExistingScores = useCallback((emptyResults) => {
    if (!existingResults || existingResults.length === 0) {
      return emptyResults;
    }

    const parsedExistingResults = parseExistingResults();
    const updatedResults = { ...emptyResults };

    Object.keys(parsedExistingResults)?.forEach(studentId => {
      if (updatedResults[studentId]) {
        const studentName = updatedResults[studentId].student_name;

        Object.keys(parsedExistingResults[studentId].subjects)?.forEach(subjectId => {
          if (updatedResults[studentId].subjects[subjectId]) {
            updatedResults[studentId].subjects[subjectId] = {
              ...updatedResults[studentId].subjects[subjectId],
              ...parsedExistingResults[studentId].subjects[subjectId]
            };
          } else {
            updatedResults[studentId].subjects[subjectId] = parsedExistingResults[studentId]?.subjects[subjectId];
          }
        });

        updatedResults[studentId].student_name = studentName;
      } else {
        const student = students.find(s => getStudentId(s) === studentId);
        const studentName = student ? getStudentName(student) : parsedExistingResults[studentId]?.student_name || "";

        updatedResults[studentId] = {
          student_id: studentId,
          student_name: studentName,
          subjects: parsedExistingResults[studentId]?.subjects
        };
      }
    });

    return updatedResults;
  }, [existingResults, parseExistingResults, students]);

  const importCAScoresFromTest = useCallback(() => {
    if (!classTestResults || classTestResults?.length === 0) {
      toast.info("No CA test results found to import");

      return;
    }

    setResults(prevResults => {
      const updatedResults = { ...prevResults };
      let importedCount = 0;

      classTestResults.forEach(testItem => {
        const studentId = getStudentId(testItem.student);
        const subjectId = getSubjectId(testItem.subject);
        const caScores = testItem.ca_scores || {};

        if (studentId && subjectId && updatedResults[studentId] && updatedResults[studentId].subjects[subjectId]) {
          // Create a new CA scores object by merging existing with imported
          const newCAScores = { ...updatedResults[studentId].subjects[subjectId].CA_scores };

          // Import only the CA scores that match our CA columns
          Object.keys(caScores).forEach(caKey => {
            // Find matching CA column from obtainable scores
            const matchingCA = caColumns.find(ca => ca.ca_type.toLowerCase() === caKey.toLowerCase().replace(/\s+/g, "")
            );

            if (matchingCA && caScores[caKey]) {
              newCAScores[matchingCA.ca_type] = caScores[caKey];
              importedCount++;
            } else {
              // If no exact match, try to find similar
              const caType = caColumns.find(ca => caKey.includes(ca.ca_type) || ca.ca_type.includes(caKey)
              )?.ca_type;

              if (caType && caScores[caKey]) {
                newCAScores[caType] = caScores[caKey];
                importedCount++;
              }
            }
          });

          // Calculate CA total from imported scores
          const caTotal = Object.values(newCAScores).reduce((sum, val) => {
            const numVal = Number(val) || 0;

            return sum + numVal;
          }, 0);

          // Update the subject result with imported CA scores
          updatedResults[studentId].subjects[subjectId] = {
            ...updatedResults[studentId].subjects[subjectId],
            CA_scores: newCAScores,
            CA_total: caTotal
          };
        }
      });

      if (importedCount > 0) {
        toast.success(`Imported ${importedCount} CA scores from tests`);
      } else {
        toast.info("No matching CA scores found to import");
      }

      return updatedResults;
    });
  }, [classTestResults, caColumns]);

  const importExamScoresFromTest = useCallback(() => {
    if (!classExamResults || classExamResults?.length === 0) {
      toast.info("No Exam results found to import");

      return;
    }

    setResults(prevResults => {
      const updatedResults = { ...prevResults };
      let importedCount = 0;

      classExamResults.forEach(testItem => {
        const studentId = getStudentId(testItem.student);
        const subjectId = getSubjectId(testItem.subject);
        const examScore = testItem.score || testItem.exam_score || 0;

        if (studentId && subjectId && updatedResults[studentId] && updatedResults[studentId].subjects[subjectId]) {
          updatedResults[studentId].subjects[subjectId] = {
            ...updatedResults[studentId].subjects[subjectId],
            exam_score: examScore
          };
          importedCount++;
        }
      });

      if (importedCount > 0) {
        toast.success(`Imported ${importedCount} Exam scores`);
      } else {
        toast.info("No matching Exam scores found to import");
      }

      return updatedResults;
    });
  }, [classExamResults]);

  useEffect(() => {
    if (open && students.length > 0 && subjects.length > 0 && !hasInitialized) {
      const emptyResults = initializeEmptyResults();

      const resultsWithExistingData = fillWithExistingScores(emptyResults);

      setResults(resultsWithExistingData);
      setHasInitialized(true);

      // // Show toast if results were pre-filled
      // if (existingResults && existingResults?.length > 0) {
      //   const prefillCount = Object.keys(resultsWithExistingData)?.reduce((count, studentId) => {
      //     return count + Object?.values(resultsWithExistingData[studentId]?.subjects)?.filter(subject => subject?.CA_total > 0 || subject?.exam_score > 0 ||
      //       Object.values(subject.CA_scores)?.some(score => Number(score) > 0)
      //     ).length;
      //   }, 0);

      //   if (prefillCount > 0) {
      //     toast.success(`Pre-filled ${prefillCount} entries from existing results`);
      //   }
      // }
    }
  }, [open, students, subjects, existingResults, initializeEmptyResults, fillWithExistingScores, hasInitialized]);

  // Import CA scores when checkbox is checked
  useEffect(() => {
    if (open && importCAScores && hasInitialized) {
      importCAScoresFromTest();
    }
  }, [open, importCAScores, hasInitialized, importCAScoresFromTest]);

  // Import Exam scores when checkbox is checked
  useEffect(() => {
    if (open && importExamScores && hasInitialized) {
      importExamScoresFromTest();
    }
  }, [open, importExamScores, hasInitialized, importExamScoresFromTest]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setResults({});
      setScoreErrors({});
      setImportCAScores(false);
      setImportExamScores(false);
      setSelectedGradingSystem(initialGradingSystemId);
      setSelectedSubject("all");
      setSearchQuery("");
      setIsSubmitting(false);
      setHasInitialized(false);
    }
  }, [open, initialGradingSystemId]);

  // Update all grades when grading system changes
  const updateAllGrades = useCallback((system) => {
    if (!system) return;

    setResults(prevResults => {
      const updatedResults = { ...prevResults };

      Object.keys(updatedResults)?.forEach(studentId => {
        Object.keys(updatedResults[studentId].subjects)?.forEach(subjectId => {
          const subjectResult = updatedResults[studentId]?.subjects[subjectId];
          const total = subjectResult?.total || 0;

          const gradeDetails = system?.grading?.find(
            g => total >= g?.min && total <= g?.max
          );

          updatedResults[studentId].subjects[subjectId] = {
            ...subjectResult,
            grade: gradeDetails?.grade || "",
            grading_system_id: system?.id
          };
        });
      });

      return updatedResults;
    });
  }, []);

  // Set grading system when selected
  useEffect(() => {
    if (selectedGradingSystem && gradingSystems?.length > 0) {
      const system = gradingSystems?.find(g => g?.id === selectedGradingSystem);
      setGradingSystem(system);

      // Update grades for all existing results
      updateAllGrades(system);
    }
  }, [selectedGradingSystem, gradingSystems, updateAllGrades]);

  // Handle CA score change
  const handleCAChange = (studentId, subjectId, caType, value) => {
    const maxScore = obtainableScores?.[0]?.ca?.find(ca => ca?.ca_type === caType)?.score || 100;
    const numericValue = Number(value) || 0;
    const errorKey = `${studentId}_${subjectId}_${caType}`;

    // Validate max score
    if (numericValue > maxScore) {
      setScoreErrors(prev => ({
        ...prev,
        [errorKey]: `${caType} cannot exceed ${maxScore}`
      }));

      // Clear error after 3 seconds
      setTimeout(() => {
        setScoreErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];

          return newErrors;
        });
      }, 3000);

      return;
    } else {
      // Clear error if valid
      setScoreErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];

        return newErrors;
      });
    }

    setResults(prevResults => {
      const updatedResults = { ...prevResults };
      const subjectResult = updatedResults[studentId]?.subjects[subjectId];

      if (subjectResult) {
        const updatedCA = {
          ...subjectResult?.CA_scores,
          [caType]: value
        };

        // Calculate CA total
        const caTotal = Object?.values(updatedCA)?.reduce((sum, val) => {
          const numVal = Number(val) || 0;

          return sum + numVal;
        }, 0);

        // Calculate total score
        const examScore = Number(subjectResult?.exam_score) || 0;
        const totalScore = caTotal + examScore;

        // Calculate grade
        let grade = "";
        if (gradingSystem?.grading) {
          const gradeDetails = gradingSystem?.grading?.find(
            g => totalScore >= g?.min && totalScore <= g?.max
          );
          grade = gradeDetails?.grade || "";
        }

        updatedResults[studentId].subjects[subjectId] = {
          ...subjectResult,
          CA_scores: updatedCA,
          CA_total: caTotal,
          total: totalScore,
          grade,
          grading_system_id: selectedGradingSystem
        };
      }

      return updatedResults;
    });
  };

  // Handle exam score change
  const handleExamChange = (studentId, subjectId, value) => {
    const maxExam = obtainableScores?.[0]?.examination || 100;
    const numericValue = Number(value) || 0;
    const errorKey = `${studentId}_${subjectId}_exam`;

    // Validate max score
    if (numericValue > maxExam) {
      setScoreErrors(prev => ({
        ...prev,
        [errorKey]: `Exam score cannot exceed ${maxExam}`
      }));

      // Clear error after 3 seconds
      setTimeout(() => {
        setScoreErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[errorKey];

          return newErrors;
        });
      }, 3000);

      return;
    } else {
      // Clear error if valid
      setScoreErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];

        return newErrors;
      });
    }

    setResults(prevResults => {
      const updatedResults = { ...prevResults };
      const subjectResult = updatedResults[studentId]?.subjects[subjectId];

      if (subjectResult) {
        const examScore = numericValue;
        const caTotal = subjectResult?.CA_total || 0;
        const totalScore = caTotal + examScore;

        // Calculate grade
        let grade = "";
        if (gradingSystem?.grading) {
          const gradeDetails = gradingSystem.grading?.find(
            g => totalScore >= g?.min && totalScore <= g?.max
          );
          grade = gradeDetails?.grade || "";
        }

        updatedResults[studentId].subjects[subjectId] = {
          ...subjectResult,
          exam_score: value,
          total: totalScore,
          grade,
          grading_system_id: selectedGradingSystem
        };
      }

      return updatedResults;
    });
  };

  // Handle grading system change
  const handleGradingSystemChange = (e) => {
    if (e.target.value === "create_new") {
      navigate("/report/grade-system");

      return;
    }
    setSelectedGradingSystem(e.target.value);
  };

  // Handle subject filter change
  const handleSubjectFilterChange = (e) => {
    setSelectedSubject(e.target.value);
  };

  // Handle search student
  const handleSearchStudent = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle import CA scores checkbox change
  const handleImportCAScoresChange = (e) => {
    const isChecked = e.target.checked;
    setImportCAScores(isChecked);

    if (isChecked) {
      // Import CA scores when checked
      importCAScoresFromTest();
    } else {
      // When unchecked, revert to original pre-filled state
      const emptyResults = initializeEmptyResults();
      const resultsWithExistingData = fillWithExistingScores(emptyResults);
      setResults(resultsWithExistingData);
      // Re-apply exam scores if checked
      if (importExamScores) {
        setTimeout(() => importExamScoresFromTest(), 0);
      }
    }
  };

  // Handle import Exam scores checkbox change
  const handleImportExamScoresChange = (e) => {
    const isChecked = e.target.checked;
    setImportExamScores(isChecked);

    if (isChecked) {
      // Import Exam scores when checked
      importExamScoresFromTest();
    } else {
      // When unchecked, revert to original pre-filled state
      const emptyResults = initializeEmptyResults();
      const resultsWithExistingData = fillWithExistingScores(emptyResults);
      setResults(resultsWithExistingData);
      // Re-apply CA scores if checked
      if (importCAScores) {
        setTimeout(() => importCAScoresFromTest(), 0);
      }
    }
  };

  // Calculate statistics
  const calculateStats = () => {
    let totalEntries = 0;
    let filledEntries = 0;
    let prefilledEntries = 0;

    Object.keys(results).forEach(studentId => {
      Object.keys(results[studentId]?.subjects)?.forEach(subjectId => {
        totalEntries++;
        const subjectResult = results[studentId]?.subjects[subjectId];

        // Check if student has at least one score entered for this subject
        const hasCAScores = Object.values(subjectResult?.CA_scores || {}).some(score => {
          const numScore = Number(score);

          return !isNaN(numScore) && numScore > 0;
        });

        const hasExamScore = Number(subjectResult?.exam_score) > 0;

        if (hasCAScores || hasExamScore) {
          filledEntries++;
        }

        // Check if this entry was pre-filled from existing results
        const hasPrefilledScores = Object.values(subjectResult?.CA_scores || {}).some(score => {
          const numScore = Number(score);

          return !isNaN(numScore) && numScore > 0;
        }) || Number(subjectResult?.exam_score) > 0 || subjectResult?.CA_total > 0;

        if (hasPrefilledScores) {
          prefilledEntries++;
        }
      });
    });

    return { totalEntries, filledEntries, prefilledEntries };
  };

  // Prepare data for submission in the required format
  const prepareSubmissionData = () => {
    if (!termId || !selectedGradingSystem || subjects.length === 0) {
      return null;
    }

    // Extract all subject IDs
    const subject_ids = subjects.map(subject => getSubjectId(subject));

    // Format students data
    const formattedStudents = [];

    Object.keys(results)?.forEach(studentId => {
      const studentData = results[studentId];
      const studentSubjects = [];

      // Collect all subjects with scores for this student
      Object.keys(studentData.subjects)?.forEach(subjectId => {
        const subjectResult = studentData?.subjects[subjectId];

        // Check if student has at least one score entered for this subject
        const hasCAScores = Object.values(subjectResult?.CA_scores || {})?.some(score => {
          const numScore = Number(score);

          return !isNaN(numScore) && numScore > 0;
        });

        const hasExamScore = Number(subjectResult.exam_score) > 0;

        if (hasCAScores || hasExamScore) {
          // Convert CA scores to numbers
          const formattedCAScores = {};
          Object.keys(subjectResult.CA_scores || {}).forEach(caType => {
            const score = subjectResult.CA_scores[caType];
            formattedCAScores[caType] = score ? Number(score) : 0;
          });

          studentSubjects.push({
            subject_id: subjectId,
            CA_total: subjectResult?.CA_total || 0,
            CA_scores: formattedCAScores,
            exam_score: Number(subjectResult?.exam_score) || 0,
            total: subjectResult?.total || 0,
            grade: subjectResult?.grade || ""
          });
        }
      });

      // Only include students who have at least one subject with scores
      if (studentSubjects?.length > 0) {
        formattedStudents?.push({
          student_id: studentId,
          subjects: studentSubjects
        });
      }
    });

    if (formattedStudents?.length === 0) {
      return null;
    }

    return {
      class_id: classId,
      term_id: termId,
      grading_system_id: selectedGradingSystem,
      grade_type: "manual",
      subject_ids,
      students: formattedStudents
    };
  };

  // Handle submit
  const handleSubmit = async () => {
    try {
      if (!selectedGradingSystem) {
        toast.error("Please select a grading system");

        return;
      }

      if (!termId) {
        toast.error("Please select a term");

        return;
      }

      if (subjects.length === 0) {
        toast.error("No subjects available");

        return;
      }

      const submissionData = prepareSubmissionData();

      if (!submissionData) {
        const stats = calculateStats();
        const pendingCount = stats.totalEntries - stats.filledEntries;

        if (pendingCount > 0) {
          toast.warning(`You have ${pendingCount} pending entries. Please fill in at least one score (CA or Exam) for each student-subject combination before saving.`, {
            autoClose: 5000
          });
        } else {
          toast.error("No scores entered to save");
        }

        return;
      }

      // Set submitting state to true
      setIsSubmitting(true);

      // Call the onSave function with formatted data
      if (onSave && typeof onSave === "function") {
        await onSave(submissionData);
      } else {
        toast.error("Save function is not available");
        setIsSubmitting(false);
      }
    } catch (error) {
      toast.error("Failed to save results");
      setIsSubmitting(false);
    }
  };

  const stats = calculateStats();

  // Combine loading states: parent's isLoading or our internal isSubmitting
  const isSaving = isLoading || isSubmitting;

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      className={classes.bulkResultModal}
      disableEscapeKeyDown={isSaving}
      disableBackdropClick={isSaving}
    >
      <Box className={classes.bulkResultModalContent}>
        <div className={classes.bulkModalHeader}>
          <div className={classes.headerTopSection}>
            <div className={classes.headerInfo}>
              <h2 className={classes.bulkModalTitle}>
                Add Student Result
              </h2>
              <div className={classes.courseInfo}>
                <span className={classes.infoItem}>
                  <strong>Class:</strong> {className || classId}
                </span>
                <span className={classes.infoItem}>
                  <strong>Term:</strong> {termName || termId || "Not Selected"}
                </span>
                <span className={classes.infoItem}>
                  <strong>Session:</strong> {session || "Not Selected"}
                </span>
                <span className={classes.infoItem}>
                  <strong>Subjects:</strong> {subjects?.length}
                </span>
                <span className={classes.infoItem}>
                  <strong>Pre-filled:</strong> {stats.prefilledEntries} entries
                </span>
              </div>
            </div>
            <IconButton onClick={onClose} className={classes.closeButton}>
              <Icon icon="mdi:close" width={24} height={24} />
            </IconButton>
          </div>
        </div>

        <div className={classes.bulkModalBody}>
          {/* Controls Section */}
          <div className={classes.bulkControls}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <SelectGroup
                  label="Grading System"
                  required
                  children={
                    <select
                      value={selectedGradingSystem}
                      onChange={handleGradingSystemChange}
                      disabled={isSaving}
                    >
                      <option value="">--- Select Grading System ---</option>
                      {gradingSystems.map(system => (
                        <option key={system?.id} value={system?.id}>
                          {system?.title}
                        </option>
                      ))}
                      <option value="create_new" style={{ color: "#155EEF", fontWeight: 500 }}>
                        + Create Grading System
                      </option>
                    </select>
                  }
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <SelectGroup
                  label="Filter by Subject"
                  children={
                    <select
                      value={selectedSubject}
                      onChange={handleSubjectFilterChange}
                      disabled={isSaving}
                    >
                      <option value="all">All Subjects</option>
                      {subjects.map(subject => (
                        <option
                          key={getSubjectId(subject)}
                          value={getSubjectId(subject)}
                        >
                          {getSubjectName(subject)}
                        </option>
                      ))}
                    </select>
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <div className={classes.searchContainer}>
                  <TableSearch
                    placeholder="Search Student..."
                    searchTableFunc={handleSearchStudent}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={3}>
                <div className={classes.checkboxContainer}>
                  <label className={classes.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={importCAScores}
                      onChange={handleImportCAScoresChange}
                      disabled={!classTestResults || classTestResults.length === 0}
                    />
                    <span>Import CA Scores from Tests</span>
                  </label>
                  {classTestResults && classTestResults.length > 0 ? (
                    <div className={classes.checkboxHint}>
                      <Icon icon="mdi:information" width={14} height={14} />
                      <small>Import {classTestResults.length} CA test results</small>
                    </div>
                  ) : (
                    <div className={classes.checkboxHint}>
                      <Icon icon="mdi:alert" width={14} height={14} />
                      <small>No CA test results found</small>
                    </div>
                  )}
                </div>

                <div className={classes.checkboxContainer} style={{ marginTop: "10px" }}>
                  <label className={classes.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={importExamScores}
                      onChange={handleImportExamScoresChange}
                      disabled={!classExamResults || classExamResults.length === 0}
                    />
                    <span>Import Exam Scores from Exams</span>
                  </label>
                  {classExamResults && classExamResults.length > 0 ? (
                    <div className={classes.checkboxHint}>
                      <Icon icon="mdi:information" width={14} height={14} />
                      <small>Import {classExamResults.length} Exam results</small>
                    </div>
                  ) : (
                    <div className={classes.checkboxHint}>
                      <Icon icon="mdi:alert" width={14} height={14} />
                      <small>No Exam results found</small>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </div>

          {/* Stats Bar */}
          <div className={classes.statsBar}>
            <div className={classes.statItem}>
              <Icon icon="mdi:account-check" width={16} height={16} style={{ color: "#4caf50" }} />
              <span>Pre-filled: {stats.prefilledEntries}</span>
            </div>
          </div>

          {/* Results Table */}
          <div className={classes.bulkTableContainer}>
            <TableContainer
              component={Paper}
              className={classes.bulkTable}
              style={{ maxHeight: "500px", overflow: "auto" }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  {/* First row: Student Name and Subject headers - Both sticky */}
                  <TableRow>
                    {/* Student Name Column - Sticky */}
                    <TableCell
                      className={classes.bulkHeaderCell}
                      rowSpan={2}
                      style={{
                        minWidth: "180px",
                        position: "sticky",
                        left: 0,
                        top: 0,
                        zIndex: 6,
                        backgroundColor: "#f5f5f5",
                        borderRight: "1px solid #e0e0e0"
                      }}
                    >
                      Student Name
                    </TableCell>

                    {/* Subject headers spanning all their assessment columns - Sticky */}
                    {filteredSubjects.map((subject, subjectIndex) => (
                      <TableCell
                        key={getSubjectId(subject)}
                        className={classes.bulkHeaderCell}
                        align="center"
                        colSpan={colSpan}
                        style={{
                          position: "sticky",
                          top: 0,
                          left: subjectIndex === 0 ? "180px" : null,
                          zIndex: 5,
                          textAlign: "center",
                          borderBottom: "1px solid #ccc",
                          fontSize: "14px",
                          fontWeight: "600",
                          backgroundColor: "#f5f5f5"
                        }}
                      >
                        {getSubjectName(subject)}
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Second row: Assessment type headers under each subject - Sticky */}
                  <TableRow>
                    {filteredSubjects.map((subject, subjectIndex) => (
                      <React.Fragment key={getSubjectId(subject)}>
                        {/* CA Score Columns for this subject */}
                        {caColumns.map((ca, caIndex) => (
                          <TableCell
                            key={`${getSubjectId(subject)}_${ca.ca_type}`}
                            className={classes.bulkHeaderCell}
                            align="center"
                            style={{
                              minWidth: "70px",
                              fontSize: "12px",
                              padding: "6px 6px",
                              position: "sticky",
                              top: "40px",
                              left: subjectIndex === 0 ?
                                `calc(180px + ${caIndex * 70}px)` : null,
                              zIndex: 4,
                              backgroundColor: "#f5f5f5"
                            }}
                          >
                            <div className={classes.columnHeader}>
                              <div className={classes.caType}>
                                {ca.ca_type} <br />
                                <small style={{ fontSize: "10px" }}>(Max: {ca.score})</small>
                              </div>
                            </div>
                          </TableCell>
                        ))}

                        {/* Total CA Column */}
                        <TableCell
                          className={classes.bulkHeaderCell}
                          align="center"
                          style={{
                            minWidth: "60px",
                            fontSize: "12px",
                            position: "sticky",
                            top: "40px",
                            left: subjectIndex === 0 ?
                              `calc(180px + ${caColumns.length * 70}px)` : null,
                            zIndex: 4,
                            backgroundColor: "#f5f5f5"
                          }}
                        >
                          <div className={classes.columnHeader}>
                            <div className={classes.caType}>Total CA</div>
                          </div>
                        </TableCell>

                        {/* Exam Column for this subject */}
                        <TableCell
                          className={classes.bulkHeaderCell}
                          align="center"
                          style={{
                            minWidth: "70px",
                            fontSize: "12px",
                            padding: "6px 6px",
                            position: "sticky",
                            top: "40px",
                            left: subjectIndex === 0 ?
                              `calc(180px + ${caColumns.length * 70 + 60}px)` : null,
                            zIndex: 4,
                            backgroundColor: "#f5f5f5"
                          }}
                        >
                          <div className={classes.columnHeader}>
                            <div className={classes.caType}>
                              Exam <br />
                              <small style={{ fontSize: "10px" }}>(Max: {obtainableScores?.[0]?.examination || 100})</small>
                            </div>
                          </div>
                        </TableCell>

                        {/* Total Column for this subject */}
                        <TableCell
                          className={classes.bulkHeaderCell}
                          align="center"
                          style={{
                            minWidth: "60px",
                            fontSize: "12px",
                            position: "sticky",
                            top: "40px",
                            left: subjectIndex === 0 ?
                              `calc(180px + ${caColumns.length * 70 + 60 + 70}px)` : null,
                            zIndex: 4,
                            backgroundColor: "#f5f5f5"
                          }}
                        >
                          <div className={classes.columnHeader}>
                            <div className={classes.caType}>Total</div>
                          </div>
                        </TableCell>

                        {/* Grade Column for this subject */}
                        <TableCell
                          className={classes.bulkHeaderCell}
                          align="center"
                          style={{
                            minWidth: "50px",
                            fontSize: "12px",
                            position: "sticky",
                            top: "40px",
                            left: subjectIndex === 0 ?
                              `calc(180px + ${caColumns?.length * 70 + 60 + 70 + 60}px)` : null,
                            zIndex: 4,
                            backgroundColor: "#f5f5f5"
                          }}
                        >
                          <div className={classes.columnHeader}>
                            <div className={classes.caType}>Grade</div>
                          </div>
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredStudents?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={filteredColSpan + 1}
                        align="center"
                        style={{ padding: "40px" }}
                      >
                        <div className={classes.noResults}>
                          <Icon icon="mdi:account-search" width={48} height={48} style={{ color: "#ccc" }} />
                          <p>{searchQuery ? `No students found matching "${searchQuery}"` : "No students to display"}</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((studentId, rowIndex) => {
                      const studentData = results[studentId];

                      return (
                        <TableRow
                          key={studentId}
                          className={rowIndex % 2 === 0 ? classes.bulkRowEven : classes.bulkRowOdd}
                        >
                          {/* Student Name - Sticky */}
                          <TableCell
                            className={classes.studentNameCell}
                            style={{
                              position: "sticky",
                              left: 0,
                              zIndex: 2,
                              backgroundColor: rowIndex % 2 === 0 ? "#fff" : "#f9f9f9",
                              minWidth: "180px",
                              fontSize: "13px",
                              borderRight: "1px solid #e0e0e0"
                            }}
                          >
                            {studentData?.student_name}
                          </TableCell>

                          {/* Scores for each filtered subject */}
                          {filteredSubjects.map((subject) => {
                            const subjectId = getSubjectId(subject);
                            const subjectResult = studentData?.subjects[subjectId] || {};
                            const hasExistingData = subjectResult?.CA_total > 0 ||
                                                   subjectResult?.exam_score > 0 ||
                                                   Object.values(subjectResult?.CA_scores || {})?.some(score => {
                                                     const numScore = Number(score);

                                                     return !isNaN(numScore) && numScore > 0;
                                                   });

                            const isPending = !hasExistingData;

                            return (
                              <React.Fragment key={`${studentId}_${subjectId}`}>
                                {/* CA Score Inputs */}
                                {caColumns.map(ca => {
                                  const errorKey = `${studentId}_${subjectId}_${ca.ca_type}`;
                                  const hasError = scoreErrors[errorKey];
                                  const inputId = `ca_${studentId}_${subjectId}_${ca.ca_type}`;
                                  const caScore = subjectResult.CA_scores?.[ca.ca_type];

                                  return (
                                    <TableCell
                                      key={`${studentId}_${subjectId}_${ca.ca_type}`}
                                      align="center"
                                      className={`${classes.bulkCell} ${isPending ? classes.pendingCell : ""}`}
                                      style={{
                                        minWidth: "70px",
                                        backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                                      }}
                                    >
                                      <Tooltip
                                        title={hasError || ""}
                                        arrow
                                        placement="top"
                                        open={!!hasError}
                                        classes={{
                                          tooltip: classes.errorTooltip,
                                          arrow: classes.errorTooltipArrow
                                        }}
                                      >
                                        <div className={classes.inputContainer}>
                                          <InputGroup
                                            inputName={inputId}
                                            inputType="number"
                                            defaultValue={caScore || ""}
                                            onChange={(e) => handleCAChange(studentId, subjectId, ca.ca_type, e.target.value)}
                                            placeHolder="0"
                                            disabled={isSaving}
                                          />
                                        </div>
                                      </Tooltip>
                                    </TableCell>
                                  );
                                })}

                                {/* Total CA Score (readonly) */}
                                <TableCell
                                  align="center"
                                  className={`${classes.totalCell} ${isPending ? classes.pendingCell : ""}`}
                                  style={{
                                    minWidth: "60px",
                                    backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                                  }}
                                >
                                  <div className={classes.totalDisplay}>
                                    {subjectResult.CA_total || 0}
                                  </div>
                                </TableCell>

                                {/* Exam Score Input */}
                                <TableCell
                                  align="center"
                                  className={`${classes.bulkCell} ${isPending ? classes.pendingCell : ""}`}
                                  style={{
                                    minWidth: "70px",
                                    backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                                  }}
                                >
                                  <Tooltip
                                    title={scoreErrors[`${studentId}_${subjectId}_exam`] || ""}
                                    arrow
                                    placement="top"
                                    open={!!scoreErrors[`${studentId}_${subjectId}_exam`]}
                                    classes={{
                                      tooltip: classes.errorTooltip,
                                      arrow: classes.errorTooltipArrow
                                    }}
                                  >
                                    <div className={classes.inputContainer}>
                                      <InputGroup
                                        inputName={`exam_${studentId}_${subjectId}`}
                                        inputType="number"
                                        defaultValue={subjectResult?.exam_score || ""}
                                        onChange={(e) => handleExamChange(studentId, subjectId, e.target.value)}
                                        placeHolder="0"
                                        className={classes.scoreInput}
                                        disabled={isSaving}
                                      />
                                    </div>
                                  </Tooltip>
                                </TableCell>

                                {/* Total Score (readonly) */}
                                <TableCell
                                  align="center"
                                  className={`${classes.totalCell} ${isPending ? classes.pendingCell : ""}`}
                                  style={{
                                    minWidth: "60px",
                                    backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                                  }}
                                >
                                  <div className={classes.totalDisplay}>
                                    {subjectResult.total || 0}
                                  </div>
                                </TableCell>

                                {/* Grade (readonly) */}
                                <TableCell
                                  align="center"
                                  className={`${classes.gradeCell} ${isPending ? classes.pendingCell : ""}`}
                                  style={{
                                    minWidth: "50px",
                                    backgroundColor: hasExistingData ? "#f0f8ff" : "inherit"
                                  }}
                                >
                                  <div className={classes.gradeDisplay}>
                                    {subjectResult.grade || "-"}
                                  </div>
                                </TableCell>
                              </React.Fragment>
                            );
                          })}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Footer */}
          <div className={classes.bulkModalFooter}>
            <div className={classes.bulkStats}>
              <div className={classes.statsGrid}>
                <div className={classes.statItem}>
                  <Icon icon="mdi:account-group" width={16} height={16} />
                  <span>Students: {filteredStudents.length}/{students.length}</span>
                </div>
                <div className={classes.statItem}>
                  <Icon icon="mdi:book-open-variant" width={16} height={16} />
                  <span>Showing: {filteredSubjects.length} of {subjects.length} subjects</span>
                </div>
                <div className={classes.statItem}>
                  <Icon icon="mdi:check-circle" width={16} height={16} />
                  <span>Filled: {stats.filledEntries}</span>
                </div>
                <div className={classes.statItem}>
                  <Icon icon="mdi:clock-outline" width={16} height={16} />
                  <span>Pending: {stats.totalEntries - stats.filledEntries}</span>
                </div>
              </div>
            </div>

            <div className={classes.actionButtons}>
              <Button
                variant="dangeroutline"
                buttonSize="medium"
                color="btndanger"
                buttonName="Cancel"
                onClick={onClose}
                // disabled={isSaving}
              />
              <Button
                variant="primary"
                buttonSize="medium"
                color="btndefault"
                buttonName="Save All Results"
                onClick={handleSubmit}
                isLoading={isLoading}
                disabled={!selectedGradingSystem || !termId || stats.filledEntries === 0 || isSaving}
              />
            </div>
          </div>
        </div>
      </Box>
    </MuiModal>
  );
};

export default BulkResultEntry;
